import { ArrowRight, Loader2, Moon, RefreshCw, Ship, TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchExportProfitability, fetchSectorRadar } from '../api';
import { ExportIndicator, ExportProfitability, SectorRadarRow } from '../types';
import GuideCard from './GuideCard';

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트 하나, 칩은 높이 18/22, 색은 dc 토큰만(보라·밝은 원색 은퇴).
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const MINICHIP = 'inline-flex items-center gap-0.5 h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';

// 채산성 판정 — 우호적은 "확실히 좋음"(ok), 비우호적은 약세(쿨), 중립은 중립칩.
const VERDICT_STYLE: Record<string, string> = {
  '우호적': 'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok',
  '중립': 'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  '비우호적': 'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
};

// 미니 스파크라인 — 최근 추이를 한 줄 SVG로. 상승=웜(dc.up)/하락=쿨(dc.down) — 앱 공통 색 관례(규격 V2).
function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const w = 100, h = 24;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  const up = data[data.length - 1] >= data[0];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-6">
      <polyline points={pts} fill="none" strokeWidth={1.5} vectorEffect="non-scaling-stroke"
        stroke={up ? '#E06C5B' : '#5C8FD6'} />
    </svg>
  );
}

function chgColor(v: number | null): string {
  if (v === null) return 'text-dc-ink3';
  return v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2';
}

function chgText(v: number | null): string {
  if (v === null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function ExportCard({ ind }: { ind: ExportIndicator }) {
  const unitTail = ind.unit === '원' || ind.unit === '억$' ? ind.unit : '';
  return (
    <div className="border-t border-dc-line1 pt-2.5">
      <div className="flex items-baseline justify-between gap-1 mb-1.5">
        <span className={`${KICKER} truncate`}>{ind.label}</span>
        {ind.unit && ind.unit.includes('=') && <span className="text-dc-ink3 text-[10.5px] shrink-0">{ind.unit}</span>}
      </div>
      <div className="tabular-nums text-[17.5px] font-[750] text-dc-ink1 leading-tight mb-1">
        {ind.value === null ? '—' : ind.value.toLocaleString('ko-KR')}
        {unitTail && <span className="text-dc-ink3 text-[10.5px] font-medium ml-0.5">{unitTail}</span>}
      </div>
      <Sparkline data={ind.spark} />
      <div className="flex flex-wrap items-center gap-x-2 mt-1.5 text-[11.5px]">
        <span className="text-dc-ink3">{ind.prev_label}</span>
        <span className={`tabular-nums ${chgColor(ind.prev_pct)}`}>{chgText(ind.prev_pct)}</span>
        {ind.yoy_pct !== null && (
          <>
            <span className="text-dc-ink3">YoY</span>
            <span className={`tabular-nums ${chgColor(ind.yoy_pct)}`}>{chgText(ind.yoy_pct)}</span>
          </>
        )}
      </div>
      {ind.as_of && <div className="text-dc-ink3/70 text-[10.5px] mt-0.5">{ind.as_of}</div>}
    </div>
  );
}

// 기울기 배지 — 상방=웜(상승색) / 하방=쿨(하락색) / 중립=중립칩.
const TILT_STYLE: Record<string, string> = {
  '상방': 'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  '중립': 'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  '하방': 'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
};

function pctColor(v: number | null): string {
  if (v === null) return 'text-dc-ink3';
  return v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2';
}

function pctText(v: number | null): string {
  if (v === null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

export default function GlobalRadarPage() {
  const [rows, setRows] = useState<SectorRadarRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exp, setExp] = useState<ExportProfitability | null>(null);
  const [expLoading, setExpLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchSectorRadar();
      setRows(r.items);
      setUpdatedAt(r.updated_at);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExp = useCallback(async () => {
    setExpLoading(true);
    try {
      const r = await fetchExportProfitability();
      setExp(r.ready ? r.data : null);
    } catch {
      setExp(null);
    } finally {
      setExpLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadExp(); }, [load, loadExp]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-3xl mx-auto w-full">
        <div className="-mx-4 -mt-2 mb-2">
          <GuideCard id="radar" title="글로벌 레이더 사용법">
            <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> 글로벌 신호를 한국 주식으로 연결하는 <b>매크로 레이어</b>예요. 스캐너가 "지금 차트 좋은 종목"을 아래에서 찾는다면, 여기는 "위에서 덮는 큰 흐름"을 봅니다.</p>
            <p className="mb-1.5"><b className="text-dc-ink2">US→KR 섹터 전이:</b> 미국장은 우리 기준 <b>어젯밤</b>에 끝나요. 어젯밤 미국 섹터가 강했으면 → 오늘 아침 같은 성격의 한국 테마를 먼저 봅니다. <b>상관이지 인과는 아니니</b> 출발점·참고용으로만 쓰세요(점수 미반영).</p>
            <p><b className="text-dc-ink2">수출 채산성:</b> 한국은행 ECOS 공식 지표(환율·교역조건·상품수지·수출입물가)로 "지금 수출이 돈이 되는가"를 봅니다. 우호적이면 수출주(반도체·자동차·방산 등)에 순풍이라는 큰 배경으로 읽으세요.</p>
          </GuideCard>
        </div>

        {/* ── ① US→KR 오버나이트 섹터 전이 ── */}
        <div className="flex items-center gap-2 mb-1">
          <Moon size={16} className="text-dc-ink3" />
          <h2 className="text-dc-ink1 text-[14px] font-[750]">어젯밤 미국 → 오늘 아침 한국</h2>
          <button
            onClick={load}
            disabled={loading}
            className={`ml-auto ${GHOST}`}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {loading ? '불러오는 중' : '새로고침'}
          </button>
        </div>
        <p className="text-dc-ink3 text-[11.5px] mb-4">
          미국 섹터 ETF의 밤사이 등락을 같은 성격의 한국 테마로 연결합니다. 많이 움직인 순.
          {updatedAt && <span className="ml-1 text-dc-ink3/70">· {new Date(updatedAt).toLocaleString('ko-KR')}</span>}
        </p>

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-20 text-dc-ink3 text-[13px]">
            <Loader2 size={16} className="animate-spin" /> 불러오는 중...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-20 text-dc-ink3 text-[13px]">데이터를 불러오지 못했습니다.</div>
        ) : (
          <div className="border-t border-dc-hair">
            {rows.map((r) => (
              <div key={r.us_etf} className="border-b border-dc-hair/80 py-3 hover:bg-white/[0.015] transition-colors">
                <div className="flex items-center gap-3">
                  {/* 미국 섹터 */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-dc-ink2 text-[13px] font-[550] truncate">{r.us_label}</span>
                      <span className="text-dc-ink3 tabular-nums text-[10.5px]">{r.us_etf}</span>
                    </div>
                    <span className={`tabular-nums text-[14px] font-[750] ${pctColor(r.change_pct)}`}>{pctText(r.change_pct)}</span>
                  </div>

                  <ArrowRight size={16} className="text-dc-ink3 shrink-0" />

                  {/* 한국 테마 */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-dc-ink1 text-[14px] font-[750] truncate">{r.kr_theme}</span>
                      {r.hit_rate != null && (r.hit_n ?? 0) >= 10 && (
                        <span className={`${MINICHIP} shrink-0 ${r.hit_rate >= 55 ? 'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok' : r.hit_rate <= 45 ? 'border-dc-line1 text-dc-ink3/70' : 'border-dc-line1 text-dc-ink3'}`}
                          title={`이 연결이 실제로 맞은 비율 — ${r.hit_n}번 중 ${r.hit_rate}% (시스템이 매일 자동 채점)`}>
                          적중 {r.hit_rate}%
                        </span>
                      )}
                      <span className={`${MINICHIP} shrink-0 ${TILT_STYLE[r.tilt] ?? TILT_STYLE['중립']}`}>
                        {r.tilt === '상방' ? <TrendingUp size={10} /> : r.tilt === '하방' ? <TrendingDown size={10} /> : null}
                        {r.tilt}
                      </span>
                    </div>
                    <span className="text-dc-ink3 text-[11.5px] truncate block">{r.kr_examples.join(' · ')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── ② 수출 채산성 (한국은행 ECOS) ── */}
        <div className="flex items-center gap-2 mt-8 mb-1">
          <Ship size={16} className="text-dc-ink3" />
          <h2 className="text-dc-ink1 text-[14px] font-[750]">수출 채산성</h2>
          {exp && (
            <span className={`${MINICHIP} ${VERDICT_STYLE[exp.verdict] ?? VERDICT_STYLE['중립']}`}>
              {exp.verdict}
            </span>
          )}
        </div>

        {expLoading && !exp ? (
          <div className="flex items-center justify-center gap-2 py-12 text-dc-ink3 text-[13px]">
            <Loader2 size={16} className="animate-spin" /> 불러오는 중...
          </div>
        ) : !exp ? (
          <div className="rounded-lg border border-dashed border-dc-line1 bg-dc-bg1/40 px-4 py-6 text-center">
            <p className="text-dc-ink2 text-[13px] mb-1">한국은행 ECOS 연동 대기</p>
            <p className="text-dc-ink3 text-[11.5px]">인증키(ECOS_API_KEY)가 없거나 아직 활성화되지 않았습니다. 활성화되면 자동으로 표시됩니다.</p>
          </div>
        ) : (
          <>
            <p className="text-dc-ink2 text-[13px] mb-1">{exp.verdict_text}</p>
            <p className="text-dc-ink3 text-[11.5px] mb-4">
              "지금 수출이 돈이 되는가"를 한국은행 공식 지표로 봅니다. 출처 {exp.source}. 참고용·점수 미반영.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-4">
              {exp.indicators.map((ind) => (
                <ExportCard key={ind.key} ind={ind} />
              ))}
            </div>
            <p className="text-dc-ink3 text-[11.5px] mt-3 leading-relaxed">
              원/달러 약세·교역조건 개선·수출물가 상승은 수출에 우호적, 수입물가 상승은 비용 압박(비우호적)으로 읽습니다.
              <TrendingDown size={11} className="inline mx-0.5 mb-0.5" />수입물가는 공급망 압박을 비춥니다.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
